import React, { useMemo, useEffect, useRef, useLayoutEffect } from 'react';
import { Lesson, Term, formatDateForRegion, doTimesOverlap, getLessonCoachIds, type Region } from '@court-sync/shared';
import { Badge } from '../ui/Badge';
import { Users, AlertTriangle, XCircle } from 'lucide-react';
import {
  getLessonSlotStylePropsForLesson,
} from '../../utils/lessonSlotColors';
import { useAgeGroupLookups } from '../../queries/ageGroups';
import {
  getPublicLessonEnrollmentCompact,
  getPublicLessonEnrollmentLabel,
} from '../../utils/publicLessonEnrollmentLabel';
import { formatCurrency } from '../../utils/currency';
import { getLessonDaysOfWeek } from '../../utils/lessonDays';
import type { LessonAvailabilityWarningDays } from '../../utils/coachAvailabilityWarnings';
import { getCoachFromCache, fetchCoachQuery } from '../../queries/coaches';
import { usePublicCoaches } from '../../queries/publicCatalog';
import { useFormatDate } from '../../hooks/useFormatDate';
import {
  TooltipProvider,
  TooltipRoot,
  TooltipTrigger,
  TooltipContent,
} from '../ui/Tooltip';
import { formatTimeHHMM, formatTimeForTooltip, formatTime, timeToMinutes, minutesToTime } from '../../utils/timeFormat';
import { isLessonFull } from '../../utils/lessonBooking';
import { earliestActiveLessonStartMinutes, scheduleScrollTopForAnchor } from '../../utils/scheduleScroll';

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** Match academy weekly calendar: narrow time gutter, wider day columns. */
const WEEKLY_GRID_CLASS = 'grid grid-cols-[2.75rem_repeat(7,minmax(6.75rem,1fr))]';
const WEEKLY_PX_PER_30_MIN = 50;

interface PreviewLesson {
  daysOfWeek: number[];
  startTime: string;
  endTime: string;
  name?: string;
}

/** Private booking: slot with stable key for overlap layout */
interface PrivateSlotKeyed {
  key: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
}

function slotsOverlapPick(a: PrivateSlotKeyed, b: PrivateSlotKeyed): boolean {
  return doTimesOverlap(a.startTime, a.endTime, b.startTime, b.endTime);
}

function getOverlappingSlotGroups(daySlots: PrivateSlotKeyed[]): PrivateSlotKeyed[][] {
  if (daySlots.length === 0) return [];
  const groups: PrivateSlotKeyed[][] = [];
  const processed = new Set<string>();
  daySlots.forEach((slot) => {
    if (processed.has(slot.key)) return;
    const group: PrivateSlotKeyed[] = [slot];
    processed.add(slot.key);
    let foundNew = true;
    while (foundNew) {
      foundNew = false;
      daySlots.forEach((other) => {
        if (processed.has(other.key)) return;
        const overlaps = group.some((g) => slotsOverlapPick(g, other));
        if (overlaps) {
          group.push(other);
          processed.add(other.key);
          foundNew = true;
        }
      });
    }
    groups.push(group);
  });
  return groups;
}

function getPrivateSlotPickStyle(
  slot: PrivateSlotKeyed,
  daySlots: PrivateSlotKeyed[],
  earliestTime: number,
  latestTime: number
): { top: string; height: string; left: string; width: string } {
  const startMinutes = timeToMinutes(slot.startTime);
  const endMinutes = timeToMinutes(slot.endTime);
  const totalRange = latestTime - earliestTime;
  const relativeStart = startMinutes - earliestTime;
  const topPercent = (relativeStart / totalRange) * 100;
  const heightPercent = ((endMinutes - startMinutes) / totalRange) * 100;

  const overlappingGroups = getOverlappingSlotGroups(daySlots);
  const group = overlappingGroups.find((g) => g.some((s) => s.key === slot.key));

  if (group && group.length > 1) {
    const groupIndex = group.findIndex((s) => s.key === slot.key);
    const groupSize = group.length;
    const gapPercent = 0.5;
    const sidePadding = 1;
    const availableWidth = 100 - sidePadding * 2 - gapPercent * (groupSize - 1);
    const widthPercent = availableWidth / groupSize;
    const leftPercent = sidePadding + groupIndex * (widthPercent + gapPercent);
    return {
      top: `${topPercent}%`,
      height: `${heightPercent}%`,
      left: `${leftPercent}%`,
      width: `${widthPercent}%`,
    };
  }
  return {
    top: `${topPercent}%`,
    height: `${heightPercent}%`,
    left: '4px',
    width: 'calc(100% - 8px)',
  };
}

function availabilityPercentStyle(
  startTime: string,
  endTime: string,
  earliestTime: number,
  latestTime: number
): { top: string; height: string } {
  const sm = timeToMinutes(startTime);
  const em = timeToMinutes(endTime);
  const totalRange = latestTime - earliestTime;
  return {
    top: `${((sm - earliestTime) / totalRange) * 100}%`,
    height: `${((em - sm) / totalRange) * 100}%`,
  };
}

const shortDateOptions: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };

interface WeeklyScheduleProps {
  lessons: Lesson[];
  terms?: Term[];
  onLessonClick?: (lesson: Lesson) => void;
  previewLesson?: PreviewLesson;
  /** Academy region for date formatting (US vs AU). When not provided, uses current academy from store. */
  region?: Region | null;
  coachAvailabilityWarnings?: LessonAvailabilityWarningDays;
  /** When true, capacity checks match academy "allow enrolment when full" (lessons are not treated as full for booking). */
  allowEnrolmentWhenFull?: boolean;
  /**
   * When true, full lessons still call onLessonClick (e.g. public page waitlist).
   * Omit or false so full slots stay non-clickable (internal schedules).
   */
  allowClickWhenFullForWaitlist?: boolean;
  /** Replaces term header when set (e.g. private booking coach view) */
  scheduleHeaderTitle?: string;
  /** Coach weekly availability windows — drawn as a light green layer */
  privateBookingAvailability?: { dayOfWeek: number; startTime: string; endTime: string }[];
  /** Valid session-length slots the user can toggle (same grid as lessons) */
  privateBookingSelectableSlots?: { dayOfWeek: number; startTime: string; endTime: string }[];
  privateBookingSelectedKeys?: string[];
  onPrivateBookingSlotToggle?: (slot: { dayOfWeek: number; startTime: string; endTime: string }) => void;
}

// Helper function to normalize dates (handles Firestore Timestamps)
const normalizeDate = (dateValue: Date | string | { toDate: () => Date } | undefined): Date | null => {
  if (!dateValue) {
    return null;
  }

  if (dateValue instanceof Date) {
    return dateValue;
  }

  if (typeof dateValue === 'string') {
    return new Date(dateValue);
  }

  // Handle Firestore Timestamp (has toDate method)
  if (
    typeof dateValue === 'object' &&
    dateValue !== null &&
    'toDate' in dateValue &&
    typeof (dateValue as { toDate: () => Date }).toDate === 'function'
  ) {
    return (dateValue as { toDate: () => Date }).toDate();
  }

  // Fallback: try to convert to Date
  try {
    return new Date(dateValue as unknown as string);
  } catch {
    return null;
  }
};

export default function WeeklySchedule({
  lessons,
  terms = [],
  onLessonClick,
  previewLesson,
  region: regionProp,
  coachAvailabilityWarnings = new Map(),
  allowEnrolmentWhenFull = false,
  allowClickWhenFullForWaitlist = false,
  scheduleHeaderTitle,
  privateBookingAvailability,
  privateBookingSelectableSlots,
  privateBookingSelectedKeys = [],
  onPrivateBookingSlotToggle,
}: WeeklyScheduleProps) {
  const { region: regionFromStore } = useFormatDate(
    regionProp != null ? { region: regionProp, publicContext: true } : undefined
  );
  const region = regionProp ?? regionFromStore ?? 'US';
  const scheduleScrollRef = useRef<HTMLDivElement>(null);
  const academyId = lessons[0]?.academyId;
  const { coaches: publicCoaches } = usePublicCoaches(academyId);
  const { ageGroupById } = useAgeGroupLookups(academyId);
  const getCoachById = getCoachFromCache;
  const fetchCoach = fetchCoachQuery;

  const coachNamesFromPublic = useMemo(() => {
    const map: Record<string, string> = {};
    for (const coach of publicCoaches) {
      map[coach.id] = `${coach.firstName} ${coach.lastName}`;
    }
    return map;
  }, [publicCoaches]);

  // Fetch coaches for lessons: try private API first (when authenticated)
  useEffect(() => {
    const coachIds = new Set<string>();
    lessons.forEach(lesson => {
      getLessonCoachIds(lesson).forEach(id => coachIds.add(id));
    });

    coachIds.forEach(coachId => {
      const coach = getCoachById(coachId);
      if (!coach && !coachNamesFromPublic[coachId]) {
        fetchCoach(coachId).catch(() => {
          // Silently fail — public coaches cache covers anonymous browse
        });
      }
    });
  }, [lessons, getCoachById, fetchCoach, publicCoaches]);

  // Helper function to get coach name (store first, then public fallback)
  const getCoachName = (coachId: string): string => {
    const coach = getCoachById(coachId);
    if (coach) return `${coach.firstName} ${coach.lastName}`;
    if (coachNamesFromPublic[coachId]) return coachNamesFromPublic[coachId];
    return 'Unknown Coach';
  };

  // Create a map of term IDs to term names
  const termMap = useMemo(() => {
    const map: { [key: string]: string } = {};
    terms.forEach(term => {
      map[term.id] = term.name;
    });
    return map;
  }, [terms]);

  // Get unique term names from lessons (excluding private lessons which don't have terms)
  const termNames = useMemo(() => {
    const termIds = new Set<string>();
    lessons.forEach(lesson => {
      if (lesson.termId && termMap[lesson.termId]) {
        termIds.add(lesson.termId);
      }
    });
    return Array.from(termIds).map(id => termMap[id]).filter(Boolean);
  }, [lessons, termMap]);

  // Get unique term objects from lessons
  const activeTerms = useMemo(() => {
    const termIds = new Set<string>();
    lessons.forEach(lesson => {
      if (lesson.termId) {
        termIds.add(lesson.termId);
      }
    });
    return terms.filter(term => termIds.has(term.id));
  }, [lessons, terms]);

  // Check if there are any private lessons (lessons without termId)
  const hasPrivateLessons = useMemo(() => {
    return lessons.some(lesson => lesson.isActive && !lesson.termId);
  }, [lessons]);

  // Generate time slots (every 30 minutes for 24 hours)
  const timeSlots = useMemo(() => {
    const slots: number[] = [];
    const startMinutes = 0; // 12:00 AM (midnight)
    const endMinutes = 24 * 60; // 12:00 AM (next day)
    const interval = 30; // 30 minutes

    for (let minutes = startMinutes; minutes < endMinutes; minutes += interval) {
      slots.push(minutes);
    }

    return slots;
  }, []);

  // Group lessons by day of week
  const lessonsByDay = useMemo(() => {
    const grouped: { [key: number]: Lesson[] } = {};

    // Initialize all days
    for (let i = 0; i < 7; i++) {
      grouped[i] = [];
    }

    // Group active lessons by daysOfWeek (support multi-day lessons)
    lessons.forEach(lesson => {
      if (lesson.isActive) {
        const days = getLessonDaysOfWeek(lesson);
        days.forEach(dayIndex => {
          grouped[dayIndex].push(lesson);
        });
      }
    });

    return grouped;
  }, [lessons]);

  // Full day (midnight–midnight); viewport is scrollable so the page does not grow.
  const { earliestTime, latestTime } = useMemo(
    () => ({ earliestTime: 0, latestTime: 24 * 60 }),
    []
  );

  const lessonAnchorMinutes = useMemo(
    () => earliestActiveLessonStartMinutes(lessons, previewLesson),
    [lessons, previewLesson]
  );

  useLayoutEffect(() => {
    const el = scheduleScrollRef.current;
    if (!el) return;
    if (lessonAnchorMinutes === null) {
      el.scrollTop = 0;
      return;
    }
    el.scrollTop = scheduleScrollTopForAnchor(el, lessonAnchorMinutes, earliestTime, latestTime);
  }, [lessonAnchorMinutes, earliestTime, latestTime]);

  // Filter time slots to visible range
  const visibleTimeSlots = useMemo(() => {
    return timeSlots.filter(slot => slot >= earliestTime && slot <= latestTime);
  }, [timeSlots, earliestTime, latestTime]);

  // Check if two lessons overlap in time (back-to-back: same end/start = no overlap)
  const lessonsOverlap = (lesson1: Lesson, lesson2: Lesson): boolean => {
    return doTimesOverlap(lesson1.startTime, lesson1.endTime, lesson2.startTime, lesson2.endTime);
  };

  // Calculate overlapping groups for lessons on the same day
  const getOverlappingGroups = (dayLessons: Lesson[]): Lesson[][] => {
    if (dayLessons.length === 0) return [];

    const groups: Lesson[][] = [];
    const processed = new Set<string>();

    dayLessons.forEach(lesson => {
      if (processed.has(lesson.id)) return;

      // Find all lessons that overlap with this one
      const group: Lesson[] = [lesson];
      processed.add(lesson.id);

      // Find all overlapping lessons
      let foundNew = true;
      while (foundNew) {
        foundNew = false;
        dayLessons.forEach(otherLesson => {
          if (processed.has(otherLesson.id)) return;

          // Check if this lesson overlaps with any lesson in the current group
          const overlaps = group.some(groupLesson => lessonsOverlap(groupLesson, otherLesson));
          if (overlaps) {
            group.push(otherLesson);
            processed.add(otherLesson.id);
            foundNew = true;
          }
        });
      }

      groups.push(group);
    });

    return groups;
  };

  // Calculate lesson position, height, and width for overlapping lessons
  const getLessonStyle = (lesson: Lesson, dayLessons: Lesson[]): {
    top: string;
    height: string;
    left: string;
    width: string;
  } => {
    const startMinutes = timeToMinutes(lesson.startTime);
    const endMinutes = timeToMinutes(lesson.endTime);
    const duration = endMinutes - startMinutes;

    // Calculate position relative to earliest time
    const relativeStart = startMinutes - earliestTime;

    // Calculate percentage positions (total range is latestTime - earliestTime)
    const totalRange = latestTime - earliestTime;
    const topPercent = (relativeStart / totalRange) * 100;
    const heightPercent = (duration / totalRange) * 100;

    // Find which overlapping group this lesson belongs to
    const overlappingGroups = getOverlappingGroups(dayLessons);
    const group = overlappingGroups.find(g => g.some(l => l.id === lesson.id));

    if (group && group.length > 1) {
      // Multiple lessons overlap - position them side by side
      const groupIndex = group.findIndex(l => l.id === lesson.id);
      const groupSize = group.length;

      // Calculate width: each lesson gets equal share of available width
      // We'll use a percentage-based approach with a small gap
      const gapPercent = 0.5; // 0.5% gap between overlapping lessons
      const sidePadding = 1; // 1% padding on each side
      const availableWidth = 100 - (sidePadding * 2) - (gapPercent * (groupSize - 1));
      const widthPercent = availableWidth / groupSize;

      // Calculate left position
      const leftPercent = sidePadding + (groupIndex * (widthPercent + gapPercent));

      return {
        top: `${topPercent}%`,
        height: `${heightPercent}%`,
        left: `${leftPercent}%`,
        width: `${widthPercent}%`,
      };
    } else {
      // Single lesson or no overlap - use full width
      return {
        top: `${topPercent}%`,
        height: `${heightPercent}%`,
        left: '4px', // left-1 equivalent
        width: 'calc(100% - 8px)', // Account for left-1 and right-1
      };
    }
  };

  const getAvailableSpots = (lesson: Lesson) => {
    if (lesson.maxCapacity === -1) return -1;
    return Math.max(0, lesson.maxCapacity - (lesson.currentEnrollment || 0));
  };

  // Check if preview lesson conflicts with existing lessons
  const hasConflict = useMemo(() => {
    if (!previewLesson || !previewLesson.startTime || !previewLesson.endTime) {
      return false;
    }

    return lessons.some(lesson => {
      if (!lesson.isActive) return false;
      if (lesson.daysOfWeek?.[0] !== previewLesson.daysOfWeek?.[0]) return false;

      return doTimesOverlap(previewLesson.startTime, previewLesson.endTime, lesson.startTime, lesson.endTime);
    });
  }, [previewLesson, lessons]);

  // Display days Monday-Sunday (1-6, 0)
  const dayOrder = [1, 2, 3, 4, 5, 6, 0];

  return (
    <TooltipProvider delayDuration={200}>
    <div className="w-full overflow-x-auto">
      <div className="mb-4">
        <div className="text-lg font-semibold text-foreground">
          {scheduleHeaderTitle && (
            <div className="mb-1 text-base font-semibold text-foreground">{scheduleHeaderTitle}</div>
          )}
          {termNames.length > 0 && (
            <div className="flex flex-col gap-1">
              <span>{termNames.length === 1 ? termNames[0] : termNames.join(', ')}</span>
              {activeTerms.length > 0 && (
                <div className="text-sm font-normal text-muted-foreground">
                  {activeTerms.map((term, index) => {
                    const startDate = normalizeDate(term.startDate);
                    const endDate = normalizeDate(term.endDate);
                    if (!startDate || !endDate) return null;

                    const startFormatted = formatDateForRegion(startDate, region, shortDateOptions);
                    const endFormatted = formatDateForRegion(endDate, region, shortDateOptions);

                    return (
                      <span key={term.id}>
                        {index > 0 && ', '}
                        {startFormatted} - {endFormatted}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          {termNames.length > 0 && hasPrivateLessons && <span className="mx-2">•</span>}
          {hasPrivateLessons && <span>Private Lessons</span>}
        </div>
      </div>

      <div className="inline-block min-w-full">
        {/*
          One scroll region + sticky header so column widths match the body (scrollbar gutter
          does not shrink only the grid below the header).
        */}
        <div className="flex h-[min(70vh,720px)] min-h-[220px] flex-col overflow-hidden rounded-lg border border-border">
          <div
            ref={scheduleScrollRef}
            className="min-h-0 flex-1 overflow-y-auto overflow-x-auto overscroll-y-contain [scrollbar-gutter:stable]"
          >
            <div className={`${WEEKLY_GRID_CLASS} sticky top-0 z-30 shrink-0 border-b border-border bg-muted`}>
              <div className="sticky left-0 z-40 border-r border-border bg-muted p-2">
                <div className="text-sm font-semibold text-foreground">Time</div>
              </div>
              {dayOrder.map((dayIndex, displayIndex) => (
                <div
                  key={dayIndex}
                  className={`border-r border-border bg-muted p-2 text-center last:border-r-0 ${displayIndex === 0 ? 'rounded-tl-none' : ''
                    }`}
                >
                  <div className="text-sm font-semibold text-foreground">
                    {DAYS_OF_WEEK[dayIndex].substring(0, 3)}
                  </div>
                </div>
              ))}
            </div>

            <div className={`${WEEKLY_GRID_CLASS} relative`} style={{ minHeight: `${((latestTime - earliestTime) / 30) * WEEKLY_PX_PER_30_MIN}px` }}>
            {/* Time labels column */}
            <div className="relative sticky left-0 z-10 border-r border-border bg-muted/40">
              {visibleTimeSlots.map((slotMinutes, index) => {
                const slotPosition = ((slotMinutes - earliestTime) / (latestTime - earliestTime)) * 100;
                // Only show time label for every hour (every 2 slots since slots are 30 min)
                const showLabel = index % 2 === 0;
                return (
                  <React.Fragment key={slotMinutes}>
                    {/* Grid line */}
                    <div
                      className="absolute left-0 right-0 border-b border-border"
                      style={{ top: `${slotPosition}%` }}
                    />
                    {/* Time label - positioned right below the grid line to align with lesson boxes */}
                    {showLabel && (
                      <div
                        className="absolute left-0 right-0 px-1 pt-0.5 text-xs text-muted-foreground"
                        style={{ top: `${slotPosition}%` }}
                      >
                        {formatTime(minutesToTime(slotMinutes))}
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            {/* Day columns */}
            {dayOrder.map((dayIndex) => {
              const dayLessons = lessonsByDay[dayIndex];

              return (
                <div
                  key={dayIndex}
                  className="relative border-r border-border bg-card last:border-r-0"
                >
                  {/* Time slot dividers */}
                  {visibleTimeSlots.map((slotMinutes) => {
                    const slotPosition = ((slotMinutes - earliestTime) / (latestTime - earliestTime)) * 100;
                    return (
                      <div
                        key={slotMinutes}
                        className="absolute left-0 right-0 border-b border-border/60"
                        style={{
                          top: `${slotPosition}%`,
                        }}
                      />
                    );
                  })}

                  {/* Private booking: coach availability (behind selectable slots) */}
                  {privateBookingAvailability
                    ?.filter((w) => w.dayOfWeek === dayIndex)
                    .map((w, wi) => {
                      const pct = availabilityPercentStyle(w.startTime, w.endTime, earliestTime, latestTime);
                      return (
                        <div
                          key={`pb-avail-${dayIndex}-${wi}`}
                          className="pointer-events-none absolute left-1 right-1 z-[1] rounded-sm bg-emerald-500/15 ring-1 ring-inset ring-emerald-500/25"
                          style={{ top: pct.top, height: pct.height }}
                          aria-hidden
                        />
                      );
                    })}

                  {/* Private booking: selectable session slots */}
                  {(() => {
                    if (!privateBookingSelectableSlots?.length || !onPrivateBookingSlotToggle) return null;
                    const daySlots: PrivateSlotKeyed[] = privateBookingSelectableSlots
                      .filter((s) => s.dayOfWeek === dayIndex)
                      .map((s) => ({
                        key: `${s.dayOfWeek}-${s.startTime}-${s.endTime}`,
                        dayOfWeek: s.dayOfWeek,
                        startTime: s.startTime,
                        endTime: s.endTime,
                      }));
                    return daySlots.map((slot) => {
                      const style = getPrivateSlotPickStyle(slot, daySlots, earliestTime, latestTime);
                      const selected = privateBookingSelectedKeys.includes(slot.key);
                      return (
                        <button
                          key={slot.key}
                          type="button"
                          aria-pressed={selected}
                          onClick={() =>
                            onPrivateBookingSlotToggle({
                              dayOfWeek: slot.dayOfWeek,
                              startTime: slot.startTime,
                              endTime: slot.endTime,
                            })
                          }
                          className={`absolute z-[6] flex flex-col justify-start rounded border-2 border-dashed p-1 text-left text-[10px] font-medium leading-tight transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                            selected
                              ? 'border-primary bg-primary/20 text-primary shadow-sm ring-2 ring-primary/40'
                              : 'border-primary/40 bg-background/80 text-foreground hover:border-primary hover:bg-primary/10'
                          }`}
                          style={{
                            top: style.top,
                            height: style.height,
                            left: style.left,
                            width: style.width,
                          }}
                        >
                          <span className="truncate">{formatTimeHHMM(slot.startTime)}</span>
                          <span className="truncate opacity-80">{formatTimeHHMM(slot.endTime)}</span>
                          {selected && <span className="mt-0.5 text-[9px] font-semibold uppercase">Selected</span>}
                        </button>
                      );
                    });
                  })()}

                  {/* Lessons */}
                  {dayLessons.map((lesson) => {
                    const style = getLessonStyle(lesson, dayLessons);
                    const isCancelled = !!lesson.cancelledFromDate;
                    const fullyBooked = isLessonFull(lesson, allowEnrolmentWhenFull);
                    const canClickFullForWaitlist = fullyBooked && allowClickWhenFullForWaitlist;
                    const lessonClickable =
                      onLessonClick && !isCancelled && (!fullyBooked || canClickFullForWaitlist);
                    const slotStyleProps =
                      !isCancelled && !fullyBooked
                        ? getLessonSlotStylePropsForLesson(lesson, ageGroupById)
                        : null;
                    return (
                      <TooltipRoot key={lesson.id}>
                        <TooltipTrigger asChild>
                          <div
                            onClick={() => {
                              if (lessonClickable) {
                                onLessonClick!(lesson);
                              }
                            }}
                            className={`absolute z-[8] rounded p-1.5 text-xs overflow-hidden flex flex-col min-h-0 min-w-0 leading-tight ${
                              isCancelled
                                ? 'border border-amber-300 dark:border-amber-700 border-l-4 border-l-amber-500 bg-amber-50 dark:bg-amber-900/20 text-amber-900 dark:text-amber-100 opacity-80'
                                : fullyBooked
                                  ? canClickFullForWaitlist
                                    ? 'border border-amber-300 dark:border-amber-700 border-l-4 border-l-amber-500 bg-amber-50 dark:bg-amber-900/25 text-amber-900 dark:text-amber-100 opacity-90 hover:opacity-100'
                                    : 'border border-red-300 dark:border-red-700 border-l-4 border-l-red-500 bg-red-100 dark:bg-red-900/30 text-red-900 dark:text-red-100 cursor-not-allowed opacity-80'
                                  : slotStyleProps?.className ?? ''
                              } ${lessonClickable ? 'cursor-pointer' : ''}`}
                            style={{
                              top: style.top,
                              height: style.height,
                              left: style.left,
                              width: style.width,
                              ...slotStyleProps?.style,
                            }}
                          >
                            <div className="font-medium mb-0.5 flex items-center gap-1.5 min-w-0 shrink-0">
                              {isCancelled && <XCircle className="h-3 w-3 flex-shrink-0 text-amber-600 dark:text-amber-400" />}
                              {(coachAvailabilityWarnings.get(lesson.id)?.has(dayIndex) ?? false) && (
                                <AlertTriangle className="h-3 w-3 flex-shrink-0 text-amber-600 dark:text-amber-400" />
                              )}
                              <span className="min-w-0 truncate">{lesson.name}</span>
                              {lesson.lessonType === 'camp' && (
                                <span className="inline-block px-1 py-0 rounded text-[10px] bg-teal-200 dark:bg-teal-800 text-teal-900 dark:text-teal-100 flex-shrink-0">
                                  Camp
                                </span>
                              )}
                            </div>
                            <div className="text-xs opacity-75 truncate min-w-0 shrink-0">
                              {formatTimeHHMM(lesson.startTime)} - {formatTimeHHMM(lesson.endTime)}
                            </div>
                            {lesson.maxCapacity !== -1 && (
                              <div className="flex items-center gap-1 mt-0.5 min-w-0 shrink-0">
                                <Users className="h-2.5 w-2.5 flex-shrink-0" />
                                <span className="text-xs min-w-0 flex-1 truncate">
                                  {getPublicLessonEnrollmentCompact(lesson)}
                                </span>
                                {fullyBooked ? (
                                  <Badge
                                    variant="secondary"
                                    className={
                                      canClickFullForWaitlist
                                        ? 'bg-amber-200 text-amber-900 dark:bg-amber-800 dark:text-amber-100 text-xs px-1 py-0 ml-auto flex-shrink-0'
                                        : 'bg-red-200 text-red-800 dark:bg-red-800 dark:text-red-100 text-xs px-1 py-0 ml-auto flex-shrink-0'
                                    }
                                  >
                                    {canClickFullForWaitlist ? 'Waitlist' : 'Full'}
                                  </Badge>
                                ) : (
                                  <Badge
                                    variant="secondary"
                                    className="bg-green-200 text-green-800 dark:bg-green-800 dark:text-green-100 text-xs px-1 py-0 ml-auto flex-shrink-0"
                                  >
                                    {getAvailableSpots(lesson)}
                                  </Badge>
                                )}
                              </div>
                            )}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent
                          side="top"
                          sideOffset={8}
                          avoidCollisions
                          collisionPadding={8}
                          className="w-64 max-w-[min(256px,90vw)] p-3 text-sm text-white bg-gray-900 dark:bg-gray-800 rounded-lg shadow-xl border-0"
                        >
                          <div className="space-y-2">
                            <div>
                              <h3 className="font-semibold text-base mb-1 flex items-center gap-2">
                                {lesson.name}
                                {lesson.cancelledFromDate && (
                                  <span className="text-xs font-normal px-2 py-0.5 bg-amber-600/80 text-amber-100 rounded">
                                    Term cancelled
                                  </span>
                                )}
                              </h3>
                              {lesson.cancelledReason && (
                                <p className="text-xs text-amber-200 dark:text-amber-300 mb-1">
                                  Reason: {lesson.cancelledReason}
                                </p>
                              )}
                              {lesson.description && (
                                <p className="text-xs text-gray-300 dark:text-gray-400 line-clamp-2">
                                  {lesson.description}
                                </p>
                              )}
                            </div>

                            <div className="space-y-1.5 pt-1 border-t border-gray-700 dark:border-gray-600">
                              <div className="flex justify-between items-center">
                                <span className="text-gray-400 dark:text-gray-500">Type:</span>
                                <span className="font-medium capitalize">{lesson.lessonType}</span>
                              </div>

                              {getLessonCoachIds(lesson).length > 0 && (
                                <div className="flex justify-between items-center">
                                  <span className="text-gray-400 dark:text-gray-500">Coach{getLessonCoachIds(lesson).length > 1 ? 'es' : ''}:</span>
                                  <span className="font-medium">{getLessonCoachIds(lesson).map(getCoachName).join(', ')}</span>
                                </div>
                              )}

                              <div className="flex justify-between items-center">
                                <span className="text-gray-400 dark:text-gray-500">Time:</span>
                                <span className="font-medium">{formatTimeForTooltip(lesson.startTime)} - {formatTimeForTooltip(lesson.endTime)}</span>
                              </div>

                              <div className="flex justify-between items-center">
                                <span className="text-gray-400 dark:text-gray-500">Price:</span>
                                <span className="font-medium">
                                  {formatCurrency(lesson.price, lesson.currency || 'AUD')}
                                </span>
                              </div>

                              {lesson.maxCapacity !== -1 && (
                                <div className="flex justify-between items-center">
                                  <span className="text-gray-400 dark:text-gray-500">Enrollment:</span>
                                  <span className="font-medium">
                                    {getPublicLessonEnrollmentLabel(lesson)}
                                  </span>
                                </div>
                              )}

                              {lesson.skillLevel && (
                                <div className="flex justify-between items-center">
                                  <span className="text-gray-400 dark:text-gray-500">Skill Level:</span>
                                  <span className="font-medium capitalize">{lesson.skillLevel}</span>
                                </div>
                              )}

                              {lesson.ageGroup && (
                                <div className="flex justify-between items-center">
                                  <span className="text-gray-400 dark:text-gray-500">Age Group:</span>
                                  <span className="font-medium capitalize">{lesson.ageGroup}</span>
                                </div>
                              )}

                            </div>
                            {(coachAvailabilityWarnings.get(lesson.id)?.has(dayIndex) ?? false) && (
                              <div className="mt-2 pt-2 border-t border-amber-600/30 flex items-start gap-2">
                                <AlertTriangle className="h-4 w-4 flex-shrink-0 text-amber-400 mt-0.5" />
                                <p className="text-xs text-amber-100">Coach is not available during this time slot</p>
                              </div>
                            )}
                          </div>
                        </TooltipContent>
                      </TooltipRoot>
                    );
                  })}

                  {/* Preview Lesson Placeholder */}
                  {previewLesson && previewLesson.daysOfWeek?.[0] === dayIndex && previewLesson.startTime && previewLesson.endTime && (() => {
                    const previewLessonObj = {
                      id: 'preview',
                      daysOfWeek: previewLesson.daysOfWeek,
                      startTime: previewLesson.startTime,
                      endTime: previewLesson.endTime,
                    } as Lesson;

                    const dayLessonsWithPreview = [...dayLessons, previewLessonObj];
                    const previewStyle = getLessonStyle(previewLessonObj, dayLessonsWithPreview);

                    return (
                      <div
                        className={`absolute rounded p-1.5 text-xs border-2 z-20 shadow-2xl overflow-hidden flex flex-col min-h-0 min-w-0 leading-tight ${hasConflict
                          ? 'border-red-500 dark:border-red-400 bg-red-100 dark:bg-red-900/60 text-red-900 dark:text-red-100 animate-pulse'
                          : 'border-dashed border-purple-400 dark:border-purple-500 bg-purple-50 dark:bg-purple-900/40 text-purple-900 dark:text-purple-100'
                          }`}
                        style={{
                          top: previewStyle.top,
                          height: previewStyle.height,
                          left: previewStyle.left,
                          width: previewStyle.width,
                        }}
                      >
                        <div className="flex items-start gap-1.5 min-w-0 min-h-0">
                          {hasConflict && (
                            <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                          )}
                          <div className="flex-1 min-w-0 overflow-hidden">
                            <div className="font-semibold truncate mb-0.5">
                              {previewLesson.name || 'New Lesson'}
                            </div>
                            <div className="text-xs opacity-90 truncate">
                              {formatTimeHHMM(previewLesson.startTime)} - {formatTimeHHMM(previewLesson.endTime)}
                            </div>
                            <div
                              className={`text-xs mt-0.5 font-medium truncate ${hasConflict
                                ? 'text-red-700 dark:text-red-300'
                                : 'opacity-75 italic'
                                }`}
                            >
                              {hasConflict ? '⚠️ Conflict Detected!' : 'Preview'}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              );
            })}
          </div>
          </div>
        </div>
      </div>
    </div>
    </TooltipProvider>
  );
}
